import { useMemo } from "react";
import { IconNameEnum } from "@b2bportal/core-themes";
import type { HotelItinerarySummary } from "@b2bportal/lodging-api";
import { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import dayjs from "dayjs";
import type { GroundBookingContext } from "@/utils/commerce/types";
import { getVehicleTitle } from "@/utils/commerce/vehicleLabels";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;

type TripReservation = HotelItinerarySummary | GroundBookingContext;

const isHotelReservation = (
  reservation: TripReservation
): reservation is HotelItinerarySummary => {
  return "lodgingSummary" in reservation;
};

export const useTripCard = ({
  reservation,
  productType,
  selectItinerary,
}: {
  reservation: TripReservation;
  productType?: Product;
  selectItinerary: () => void;
}) => {
  const { t, formatDateTime } = useI18nContext();
  const isHotel =
    productType === Product.Hotel || isHotelReservation(reservation);

  // Format hotel data inline to avoid calling hooks conditionally
  const hotelData = useMemo(() => {
    if (!isHotel) return null;

    const hotelReservation = reservation as HotelItinerarySummary;
    const checkInDate = formatDateTime(
      dayjs(hotelReservation.checkInDate).toDate(),
      DATE_FORMAT
    );
    const checkOutDate = formatDateTime(
      dayjs(hotelReservation.checkOutDate).toDate(),
      DATE_FORMAT
    );

    return {
      handlers: { selectItinerary },
      text: {
        product: t("core-hotels.postBooking.product"),
        title: hotelReservation.lodgingSummary?.name ?? "",
        location: t("core-hotels.postBooking.summaryCard.location", {
          city: hotelReservation.lodgingSummary?.city,
          country: hotelReservation.lodgingSummary?.country,
        }),
        dates: t("core-hotels.postBooking.summaryCard.dates", {
          fromDate: checkInDate,
          untilDate: checkOutDate,
        }),
      },
    };
  }, [isHotel, reservation, selectItinerary, t, formatDateTime]);

  // Format car data
  const carData = useMemo(() => {
    if (isHotel) return null;

    const carReservation = reservation as GroundBookingContext;
    const pickupDate = formatDateTime(
      dayjs(carReservation.productDetails.pickUp.dateTime).toDate(),
      DATE_FORMAT
    );
    const dropoffDate = formatDateTime(
      dayjs(carReservation.productDetails.dropOff.dateTime).toDate(),
      DATE_FORMAT
    );

    return {
      handlers: { selectItinerary },
      text: {
        product: t("trips.tripCard.carRental"),
        title: getVehicleTitle(
          t,
          carReservation.productDetails.vehicle.category,
          carReservation.productDetails.vehicle.vehicleType,
          carReservation.productDetails.vehicle.name
        ),
        location: carReservation.productDetails.pickUp.location.name ?? "",
        dates: `${pickupDate} - ${dropoffDate}`,
      },
    };
  }, [isHotel, reservation, selectItinerary, t, formatDateTime]);

  // Type-safe access: exactly one of hotelData or carData is non-null based on isHotel
  const data = isHotel ? hotelData : carData;

  return useMemo(
    () => ({
      handlers: data!.handlers,
      text: data!.text,
      icon: isHotel
        ? IconNameEnum.hotelReservation
        : IconNameEnum.carReservation,
    }),
    [isHotel, data]
  );
};
